import { Formik, FormikErrors, FormikHelpers } from "formik";
import { AwardStatus, College, ReferenceItemDTO, StudentAward, StudentAwardProfile } from "../../../api/client";
import { ArrowRightCircle, Check, X } from "react-bootstrap-icons";
import Combobox from "../../../components/UiComponents/combobox";
import CustomSelect from "../../../components/UiComponents/selectFieldGroup";
import { awardStatusOptions } from "../../Student/Onboarding/Helpers";
import { Input } from "../../../components/UiComponents/input";
import { Button } from "../../../components/UiComponents/button";

export interface StudentAwardForm {
  netId: string | null;
  startAcademicYear: number | null;
  collegeId: string | null;
  awardId: string | null;
  programId: string| null;
  programDetails: string;
  isUndergraduate: boolean;
  estimatedAmount: number | null;
  awardStatus: AwardStatus;
}

interface AddStudentAwardProps {
  isOpen: boolean; // used if used as a modal; otherwise does nothing really
  onClose: () => void; // used if used as a modal; otherwise does nothing
  isModal?: boolean; // If we want to display this as a modal or a section (for admin dashboard)
  onSubmit: (form: StudentAwardForm) => Promise<void>;
  awards: ReferenceItemDTO[]; // holds list of awards
  programs: ReferenceItemDTO[]; // hold list of programs
  colleges: College[]; // hold list of colleges
  currentStudentID? : string; // used if used as a modal; will pre-fill the netID and disable the field
  initialData?: StudentAwardProfile | null; // if editing an award, prefills data
}

// very rudimentary validation, would be nice to replace with Yup!
// I'm also aware we have a validator with jsons, but it has weird error formatting right now + this was a lot easier for me to deal with for now
// ps: pls let us use Yup
const validate = (values: StudentAwardForm) => {
    const errors: FormikErrors<StudentAwardForm> = {};
    if(!values.netId) {
      errors.netId = "A valid netID is required.";
    }

    if (!values.startAcademicYear || values.startAcademicYear < 1000 || values.startAcademicYear > 9999 || isNaN(Number(values.startAcademicYear)))
        errors.startAcademicYear = "A valid academic year is required.";
    if (!values.collegeId) errors.collegeId = "College is required.";
    if (!values.awardId) errors.awardId = "Award is required.";
    if (!values.programId) errors.programId = "Program is required.";
    return errors;
};

// This component is set up to (ideally) also work as a modal so it can be more compact on the student profile
// On the admin page however, this displays as a regular section, with no way to close it!
export default function AddStudentAwardModal({isOpen, onClose, onSubmit, awards, programs, colleges, isModal = false, currentStudentID, initialData}: AddStudentAwardProps){
  
  if (!isOpen && isModal) return null;

  const isEditing = !!initialData

  // Default values for form
  const initialValues: StudentAwardForm = {
    netId: currentStudentID ?? "",
    startAcademicYear: initialData?.academicYear ?? null,
    collegeId: initialData?.college?.id ?? null,
    awardId: initialData?.award?.id ?? null,
    programId: initialData?.program?.id ?? null,
    programDetails: initialData?.programDetails ?? "",
    isUndergraduate: initialData?.isUndergraduate ?? true,
    estimatedAmount: initialData?.estimatedAmount ?? null,
    awardStatus: initialData?.awardStatus ?? AwardStatus.Accepted
  }

  const handleSubmit = async (
    values: StudentAwardForm,
    { setSubmitting, resetForm }: FormikHelpers<StudentAwardForm>
  ) => {
    await onSubmit(values)
      .then(() => {
        resetForm();
        onClose()
      }).catch(() => {
      }).finally(() => {
        setSubmitting(false);
      });
  };

  // Close(if modal)/clear form
  const handleClose = (resetForm: () => void) => {
    resetForm();
    onClose();
  };

  const backdropClass = isModal ? `fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50` : 'w-full'

  const containerClass = `relative w-full bg-white dark:bg-gray-600 rounded-2xl shadow-xl p-6 ${isModal ? 'max-w-2xl max-h-[90vh] overflow-y-auto' : ''}`

  return (
    <div className={backdropClass} onClick={(e) => {if (isModal && e.target === e.currentTarget) {onClose()}}}>
      <div className={containerClass}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validate}
          enableReinitialize
        >
          {({ values, setFieldValue, handleSubmit, isSubmitting, resetForm }) => (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                  {isEditing ? "Edit Student Award" : "Add Student Award"}
                </h2>
                {isModal && (<Button
                  variant="ghost"
                  type="button"
                  onClick={() => handleClose(resetForm)}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" onClick={() => {onClose()}}/>
                </Button>)}
              </div>

              {/* netID */}
              <Input
                title="NetID"
                name="netId"
                type="text"
                required
                placeholder="ex. netid123"
                value={values.netId ?? ""}
                disabled={currentStudentID != null}
              />
              {/* Academic Year */}
              <Input
                title="Start Academic Year"
                name="startAcademicYear"
                type="number"
                required
                placeholder="ex. 2023"
                value={values.startAcademicYear ?? ""}
              />
              {/* College */}
              <Combobox
                label="College"
                name="collegeId"
                required
                options={colleges.map((c) => ({id: c.id!, name: c.name!}))}
                value={values.collegeId}
                onChange={(id) => setFieldValue("collegeId", id)}
                placeholder="Select College..."
              />
            {/* Award */}
              <Combobox
                label="Award"
                name="awardId"
                required
                options={awards.map((a) => ({id: a.id!, name: a.name!}))}
                value={values.awardId}
                onChange={(id) => setFieldValue("awardId", id)}
                placeholder="Select Award..."
              />
              {/* Program */}
              <Combobox
                label="Program"
                name="programId"
                required
                options={programs.map((p) => ({id: p.id!, name: p.name!}))}
                value={values.programId}
                onChange={(id) => setFieldValue("programId", id)}
                placeholder="Select Program..."
              />
              {/* Award Status (Accepted, Honorable Mention, Declined) */}
              <CustomSelect
                label="Award Status"
                options={awardStatusOptions}
                value={values.awardStatus.toString()}
                onChange={(val) => setFieldValue("awardStatus", parseInt(val))}
                required
                name={"awardStatus"}
              />
              {/* Program details */}
              <Input
                title="Program Details"
                name="programDetails"
                type="text"
                placeholder="ex. Study Abroad in Japan"
              />
              {/* Estimated Amount */}
              <Input
                title="Estimated Amount"
                name="estimatedAmount"
                type="number"
                placeholder="ex. 5000"
                value={values.estimatedAmount ?? ""}
              />
              {/* IsUndergraduate */}
              <label className="flex items-center gap-3 cursor-pointer select-none" 
                onClick={() => setFieldValue("isUndergraduate", !values.isUndergraduate)}>
                  <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                          ${values.isUndergraduate
                              ? "bg-usfGreen border-usfGreen"
                              : "border-gray-400 dark:border-gray-500 bg-transparent"}`}
                  >
                      {values.isUndergraduate && <Check className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Is Undergraduate?
                  </span>
              </label>

              {/* Footer */}
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                  <Button
                      type="button"
                      onClick={() => handleClose(resetForm)}
                      variant="outline"
                      className="font-normal"
                      abbr={isModal ? "Close the form" : "Clear the form"}
                  >
                      {isModal ? "Close" : "Clear"}
                  </Button>
                  <Button
                      type="submit"
                      variant="default"
                      disabled={isSubmitting}
                      abbr={isSubmitting ? "Button is disabled" : "Click to submit form and add award"} // no abbr text when button is disabled
                  >
                      {isSubmitting ? "Saving..." : (isEditing ? "Update Award" : "Add Award")}
                      <ArrowRightCircle className="w-5 h-5 ml-2"/>
                  </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  )
}